import { NodeProps, Position } from "@xyflow/react";
import React, { MouseEvent, useMemo } from "react";
import { RiAddLine } from "@remixicon/react";
import classNames from "classnames";
import Styles from "./Nodes.module.scss";
import BaseHandle from "../BaseHandle";
import {
    GROUP, GROUP_ID_ICON_MAP, GROUP_ID_TYPE_MAP,
} from "../../workflow/nodes";
import { useWorkflow, WorkflowStepGroupWithNodeData } from "../../providers/WorkflowProvider";
import {
    WorkflowServiceChildAction, WorkflowStepGroup,
} from "../../../../__generated__/api";
import { useWorkflowSidebar } from "../../providers/WorkflowSidebarProvider";
import Button from "../../../theme/button/Button";
import Icon from "../../../theme/icon/Icon";
import Badge from "../../../theme/badge/Badge";

type GroupStepNode = WorkflowStepGroup & Omit<WorkflowServiceChildAction, "__typename">;

export default function BaseGroupNode(props: NodeProps) {
    const {
        id, type, data,
    } = props;
    const stepGroup = useMemo(() => data as GroupStepNode, [data]);

    const {
        setCurrentStep,
        addTemporaryNode,
        selectedNode,
        temporaryNodeId,
        clearSelection,
        collapsedGroups,
        setCollapsedGroups,
    } = useWorkflow();
    const { toggleSidebarVisibility } = useWorkflowSidebar();

    const typeName = GROUP_ID_TYPE_MAP[type];
    const isConditionGroupNode = typeName === GROUP.CONDITION;
    const title = stepGroup.label;
    const childrenCount = isConditionGroupNode ? 2 : stepGroup.children?.length ?? 0;
    const isCollapsed = collapsedGroups.has(id);

    const GroupIcon = GROUP_ID_ICON_MAP[type];

    const handleAddNode = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        // Make sure group node is expanded before adding a new node
        if (collapsedGroups.has(id)) {
            setCollapsedGroups(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }

        clearSelection();
        addTemporaryNode({ nodeId: id });
        setCurrentStep({ ...stepGroup, id, parentId: null } as unknown as WorkflowStepGroupWithNodeData);
        toggleSidebarVisibility(true);
    };

    // only show add node button if the node is not a condition group node or if the selected node is not the temporary node
    const isTemporaryNodeActive = selectedNode === null && temporaryNodeId !== null;
    const shouldShowAddNodeButton = !isConditionGroupNode && !isTemporaryNodeActive;

    return (
        <div
            className={classNames(Styles["actions-group-node-wrapper"], {
                nonDraggableNode: true,
            })}
        >
            <BaseHandle type="target" position={Position.Left} id="a" />
            <div
                className={classNames(Styles["base-group-node"], Styles[`group-node-${typeName}`])}
            >
                {
                    GroupIcon !== undefined && (
                        <Icon
                            icon={(
                                <GroupIcon
                                    size="xsmall"
                                    className={classNames(Styles[`group-node-icon-${typeName}`], "group-node-icon")}
                                />
                            )}
                        />
                    )
                }
                <p className={Styles["group-node-title"]}>
                    {title}
                    {isCollapsed && childrenCount > 0 && (
                        <Badge
                            size="small"
                            isRounded
                            className={classNames(Styles[`group-node-badge-${typeName}`], Styles["group-node-badge"])}
                        >
                            {childrenCount}
                        </Badge>
                    )}
                </p>
            </div>
            <BaseHandle
                type="source"
                position={Position.Bottom}
                id="b"
            />
            {
                shouldShowAddNodeButton && (
                    <Button
                        variant="none"
                        className={Styles["actions-group-node-overlay"]}
                        onClick={handleAddNode}
                    >
                        <RiAddLine className={Styles["add-node-icon"]} />
                    </Button>
                )
            }
        </div>
    );
}
